/**
 * RevenueKpiRow — the eight key figures of ADMIN-PAGE-PLAN.md §Page sections 1 (card 1242).
 *
 * Presentational and pure: it renders the `orders` object of
 * `GET /admin/revenue-calculation/summary` and derives nothing. Every money figure is the
 * API's metric object `{ value, basis, source, dataQuality, blockedBy }`, so a figure the
 * business has not ratified arrives as `value: null` and leaves here as `— / Missing`.
 *
 * 🔴 TWO OF THESE EIGHT ARE BLOCKED TODAY AND MUST LOOK IT:
 *   · Net settled revenue — Q3, the reserve % / holder / release window are unknown.
 *     Rendering it equal to gross revenue would be a lie with a plausible face.
 *   · Static expense (and therefore fully-loaded profit and margin) — Q5, the DrCare and
 *     DoctorNow monthly provider fees do not exist in the repo, the DB or settings.
 * ⛔ Do not "fix" either by substituting 0. A zero cost produces a 100% margin, which is
 *    the single most expensive wrong number this page could print.
 */

import { formatPrice } from '../../utils/formatters';
import {
  isBlockedMetric,
  formatMetricValue
} from '../../utils/revenueMetrics';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/**
 * The eight figures in render order. Pure data — the row maps this list.
 * `secondaryKey` pairs a margin onto its profit tile: fully-loaded profit and
 * fully-loaded margin are one figure with two readings, and they block together.
 */
export const KPI_DEFS = Object.freeze([
  { key: 'grossRevenue', label: 'Gross revenue', kind: 'money' },
  { key: 'netSettledRevenue', label: 'Net settled revenue', kind: 'money' },
  { key: 'totalVariableExpense', label: 'Variable expense', kind: 'money' },
  { key: 'contributionProfit', label: 'Contribution profit', kind: 'money' },
  { key: 'contributionMargin', label: 'Contribution margin', kind: 'percent' },
  { key: 'staticProviderExpenseAllocated', label: 'Static expense', kind: 'money' },
  {
    key: 'fullyLoadedProfit',
    label: 'Fully-loaded profit',
    kind: 'money',
    secondaryKey: 'fullyLoadedMargin',
    secondaryKind: 'percent'
  }
]);

/** Pure. One key figure. A blocked figure is styled AND labelled as blocked. */
const KpiTile = ({ def, metric, secondary }) => {
  const blocked = isBlockedMetric(metric);
  return (
    <div
      className={styles.kpiTile}
      data-testid={`kpi-${def.key}`}
      data-blocked={blocked ? 'true' : 'false'}
      title={metric?.basis ?? ''}
    >
      <p className={styles.kpiLabel}>{def.label}</p>
      <p className={blocked ? styles.kpiValueBlocked : styles.kpiValue}>
        {formatMetricValue(metric, def.kind)}
      </p>
      {def.secondaryKey && (
        <p className={styles.kpiSecondary}>
          {formatMetricValue(secondary, def.secondaryKind)} margin
        </p>
      )}
      <span className={blocked ? styles.chipBlocked : styles.chipOk}>
        {blocked
          ? `Blocked — ${metric?.blockedBy ?? 'input missing'}`
          : (metric?.dataQuality ?? 'derived')}
      </span>
    </div>
  );
};

/**
 * Pure. The size of the hole: revenue booked against no stored medication cost.
 * These are precisely the rows that WOULD read as 100% margin if a blocked input were
 * ever allowed to become 0, so the count is surfaced rather than absorbed.
 */
const ExposureTile = ({ exposure }) => {
  const orderCount = Number(exposure?.orderCount ?? 0);
  return (
    <div
      className={styles.kpiTile}
      data-testid="kpi-missingCostExposure"
      data-blocked={orderCount > 0 ? 'true' : 'false'}
      title={exposure?.basis ?? ''}
    >
      <p className={styles.kpiLabel}>Missing-cost exposure</p>
      <p className={styles.kpiValue}>{formatPrice(Number(exposure?.grossRevenueCents ?? 0))}</p>
      <p className={styles.kpiSecondary}>{orderCount} orders with no stored cost</p>
      <span className={orderCount > 0 ? styles.chipBlocked : styles.chipOk}>
        {orderCount > 0 ? 'Would read as 100% margin' : 'No exposure'}
      </span>
    </div>
  );
};

/**
 * The KPI row. `orders` is the summary payload's `orders` object; `truncated` is its
 * row-cap flag, reported rather than hidden because a truncated total understates revenue.
 */
const RevenueKpiRow = ({ orders, truncated = false, scopeNote = null }) => {
  const orderCount = Number(orders?.orderCount ?? 0);
  return (
    <div data-testid="kpi-content">
      {orderCount === 0 && (
        <p className={styles.notice} data-testid="kpi-no-orders">
          No orders in the selected range. Order figures below are $0.00 because nothing was
          booked in this window — not because a cost is unknown. Figures that depend on a
          blocked input still read {formatMetricValue(null, 'money')}.
        </p>
      )}
      {truncated && (
        <p className={styles.notice} data-testid="kpi-truncated">
          Row cap reached — these totals are truncated and understate the range.
        </p>
      )}
      <div className={styles.kpiGrid} data-testid="kpi-grid">
        {KPI_DEFS.map((def) => (
          <KpiTile
            key={def.key}
            def={def}
            metric={orders?.[def.key]}
            secondary={def.secondaryKey ? orders?.[def.secondaryKey] : null}
          />
        ))}
        <ExposureTile exposure={orders?.missingCostExposure} />
      </div>
      {scopeNote && (
        <p className={styles.scopeLine} data-testid="kpi-scope">{scopeNote}</p>
      )}
    </div>
  );
};

export default RevenueKpiRow;
